import { Building2, Check, Loader2, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  adminClient,
  type AdminOrganizationRow,
  type AdminStudioSeatRole,
  type StudioPersonOrganization,
} from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { orgLabel } from '@/lib/format';
import { orgSectionPath } from '@/lib/orgNav';
import { cn } from '@/lib/utils';

const COLUMNS = [{ label: 'Organization' }, { label: 'Studio access' }, { label: '', className: 'w-40' }];

function RoleSelect({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: AdminStudioSeatRole;
  disabled?: boolean;
  onChange: (role: AdminStudioSeatRole) => void;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as AdminStudioSeatRole)} disabled={disabled}>
      <SelectTrigger id={id} className="h-8 w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="owner">Owner</SelectItem>
        <SelectItem value="admin">Admin</SelectItem>
        <SelectItem value="member">Member</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * The organizations a person belongs to and their Studio access in each, so
 * access can be given from the person as well as from the organization.
 */
export function PersonOrganizations({ userId, personName }: { userId: string; personName: string }) {
  const [rows, setRows] = useState<StudioPersonOrganization[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busyOrg, setBusyOrg] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<StudioPersonOrganization | undefined>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      setRows((await adminClient.getPersonOrganizations(userId)).organizations);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load organizations');
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const grant = async (organization: StudioPersonOrganization, role: AdminStudioSeatRole) => {
    setBusyOrg(organization.id);
    try {
      await adminClient.grantStudioSeat(organization.id, { userId, role });
      toast.success(
        organization.seat
          ? `${personName} is now ${role === 'admin' ? 'an' : 'a'} ${role} in ${organization.name}`
          : `${personName} can now open Studio in ${organization.name}`,
      );
      await load();
    } catch (grantError) {
      toast.error(grantError instanceof Error ? grantError.message : 'Failed to update access');
    } finally {
      setBusyOrg(undefined);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusyOrg(removing.id);
    try {
      await adminClient.revokeStudioSeat(removing.id, userId);
      toast.success(`Removed ${personName}'s access to ${removing.name}`);
      setRemoving(undefined);
      await load();
    } catch (removeError) {
      toast.error(removeError instanceof Error ? removeError.message : 'Failed to remove access');
    } finally {
      setBusyOrg(undefined);
    }
  };

  return (
    <AdminSection
      title="Organizations"
      description={`Every organization ${personName} belongs to, and their Studio access in each.`}
      actions={
        <Button type="button" size="sm" onClick={() => setAdding(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Add to organization
        </Button>
      }
      bodyClassName="p-0"
    >
      <DataTable>
        <DataTableHead columns={COLUMNS} />
        {error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
        ) : !rows ? (
          <DataTableMessage colSpan={COLUMNS.length}>Loading organizations</DataTableMessage>
        ) : rows.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>{personName} isn't in any Studio organization yet.</DataTableMessage>
        ) : (
          <tbody>
            {rows.map((organization) => {
              const busy = busyOrg === organization.id;
              return (
                <DataTableRow key={organization.id}>
                  <DataTableCell>
                    <div className="flex items-center gap-2">
                      <Link to={orgSectionPath(organization.id, 'members')} className="font-medium text-foreground hover:underline">
                        {organization.name}
                      </Link>
                      {organization.internal ? <StatusPill tone="neutral">RapidSOS</StatusPill> : null}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    {organization.seat ? (
                      <div className="flex items-center gap-2">
                        <RoleSelect
                          id={`role-${organization.id}`}
                          value={organization.seat.role}
                          disabled={busy}
                          onChange={(role) => role !== organization.seat?.role && void grant(organization, role)}
                        />
                        {organization.seat.active ? null : <StatusPill tone="warning">Inactive</StatusPill>}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">No Studio access</span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    {busy ? (
                      <Loader2 className="ml-auto h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                    ) : organization.seat ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => setRemoving(organization)}>
                        Remove access
                      </Button>
                    ) : (
                      <Button type="button" size="sm" variant="outline" onClick={() => void grant(organization, 'member')}>
                        Give access
                      </Button>
                    )}
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>

      <AddToOrganizationDialog
        open={adding}
        userId={userId}
        personName={personName}
        hasSeatIn={new Set((rows ?? []).filter((row) => row.seat).map((row) => row.id))}
        onClose={() => setAdding(false)}
        onAdded={() => {
          setAdding(false);
          void load();
        }}
      />

      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(undefined)}
        title="Remove Studio access?"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setRemoving(undefined)} disabled={Boolean(busyOrg)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void remove()} disabled={Boolean(busyOrg)}>
              Remove access
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {removing ? `${personName} loses access to Studio in ${removing.name}. Their projects stay, and they stay in the organization.` : null}
        </p>
      </Dialog>
    </AdminSection>
  );
}

function AddToOrganizationDialog({
  open,
  userId,
  personName,
  hasSeatIn,
  onClose,
  onAdded,
}: {
  open: boolean;
  userId: string;
  personName: string;
  hasSeatIn: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[] | undefined>();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<AdminOrganizationRow | undefined>();
  const [role, setRole] = useState<AdminStudioSeatRole>('member');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setChosen(undefined);
    setRole('member');
    if (!organizations) {
      adminClient
        .listOrganizations()
        .then((result) => setOrganizations(result.items))
        .catch((error: unknown) => toast.error(error instanceof Error ? error.message : 'Could not load organizations'));
    }
  }, [open, organizations]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (organizations ?? [])
      .filter((organization) => !hasSeatIn.has(organization.organizationId))
      .filter(
        (organization) =>
          !needle ||
          orgLabel(organization).toLowerCase().includes(needle) ||
          organization.organizationId.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [organizations, query, hasSeatIn]);

  const submit = async () => {
    if (!chosen) {
      toast.error('Pick an organization');
      return;
    }
    setBusy(true);
    try {
      const result = await adminClient.grantStudioSeat(chosen.organizationId, { userId, role, joinOrganization: true });
      toast.success(
        result.joinedOrganization
          ? `Added ${personName} to ${orgLabel(chosen)} with Studio access`
          : `${personName} can now open Studio in ${orgLabel(chosen)}`,
      );
      onAdded();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add to the organization');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add to organization"
      description={`${personName} joins the organization if they aren't in it yet, and gets Studio access right away.`}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="add-to-org-form" disabled={busy || !chosen}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Add
          </Button>
        </>
      }
    >
      <form
        id="add-to-org-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div>
          <Label htmlFor="add-to-org-search">Organization</Label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              id="add-to-org-search"
              className="pl-9"
              type="text"
              autoComplete="off"
              placeholder="Search by name or id"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          {!organizations ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading organizations
            </div>
          ) : matches.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No organization matches.</p>
          ) : (
            <ul role="listbox" aria-label="Organizations">
              {matches.map((organization) => {
                const selected = chosen?.organizationId === organization.organizationId;
                return (
                  <li key={organization.organizationId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => setChosen(organization)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary',
                        selected && 'bg-secondary',
                      )}
                    >
                      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">{orgLabel(organization)}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">{organization.organizationId}</span>
                      </span>
                      {organization.studioAccess ? null : <StatusPill tone="neutral">Studio off</StatusPill>}
                      {selected ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <Label htmlFor="add-to-org-role">Role</Label>
          <Select value={role} onValueChange={(next) => setRole(next as AdminStudioSeatRole)}>
            <SelectTrigger id="add-to-org-role" className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </form>
    </Dialog>
  );
}
